import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { FarbPunktSchalter } from "../components/Farbe";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Play, Square, RotateCcw, Rocket, Plus, Trash2, ScrollText, AlertTriangle, ExternalLink, Truck, Lock, Eye, Copy, Check } from 'lucide-react';
import { instances, deployments, type AuftragAngelegt, type AusrollBefund, products, applications } from '../api';
import { AusrollPanel } from '../components/AusrollPanel';
import { useAuth } from '../contexts/AuthContext';
import StatusBadge from '../components/StatusBadge';
import ActivationPanel from '../components/ActivationPanel';
import TenantsPanel from '../components/TenantsPanel';
import AppNutzerPanel from '../components/AppNutzerPanel';
import InstanceLinksPanel from '../components/InstanceLinksPanel';
import VerknuepfungsLeiste from '../components/VerknuepfungsLeiste';
import MedusaSchluesselKarte from '../components/MedusaSchluesselKarte';
import ComposePanel from '../components/ComposePanel';
import MetrikPanel from '../components/MetrikPanel';
import BildtagPanel from '../components/BildtagPanel';
import DemoInhalteKarte from '../components/DemoInhalteKarte';
import AdressenKarte from '../components/AdressenKarte';
import VerbundKnotenKarte from '../components/VerbundKnotenKarte';
import BitwardenKnopf from '../components/BitwardenKnopf';
import InstanzEntfernenModal from '../components/InstanzEntfernenModal';
import UmzugModal from '../components/UmzugModal';
import SqlKonsole from '../components/SqlKonsole';
import ZugaengePanel from '../components/ZugaengePanel';
import BryveNotfallPanel from '../components/BryveNotfallPanel';
import BryveTfaPanel from '../components/BryveTfaPanel';
import StatistikPanel from '../components/StatistikPanel';
import UploadGrenzeKarte from '../components/UploadGrenzeKarte';
import SicherungenPanel from '../components/SicherungenPanel';
import MailGestaltungPanel from '../components/MailGestaltungPanel';
import {
  UMGEBUNGEN,
  umgebung,
  instanzAdresse,
  SETZBARE_ZUSTAENDE,
  istSetzbar,
  type SetzbarerZustand,
} from '../umgebungen';
import type { InstanceEnvironment, ZustandsBefund } from '../types';
import JobVerlauf from '../components/JobVerlauf';
import DiagnosePanel from '../components/DiagnosePanel';
import ProduktKonfigKarte from '../components/ProduktKonfigKarte';
import DatenStaendePanel from '../components/DatenStaendePanel';
import { format } from 'date-fns';

type Tab = 'overview' | 'jobs' | 'secrets' | 'config' | 'activation' | 'tenants' | 'links' | 'metrics' | 'sql' | 'zugaenge' | 'appnutzer' | 'statistik' | 'compose' | 'sicherungen' | 'mailgestaltung' | 'diagnose' | 'datenstaende';

/**
 * Dieselben Werte noch einmal zur Laufzeit.
 *
 * Aus der Adresse kommt eine Zeichenkette, und die muss geprueft werden,
 * bevor sie ein `Tab` sein darf. Welche Reiter eine Instanz tatsaechlich
 * zeigt, entscheidet weiter unten die Liste `tabs` — hier geht es nur darum,
 * dass kein beliebiger Text in den Zustand gelangt.
 */
const ALLE_REITER: Tab[] = [
  'overview', 'jobs', 'secrets', 'config', 'activation', 'tenants',
  'links', 'metrics', 'sql', 'zugaenge', 'appnutzer', 'statistik', 'compose',
  'sicherungen', 'mailgestaltung', 'diagnose', 'datenstaende',
];

/**
 * Geheimnisse, deren Wechsel Daten unlesbar macht — nicht nur abmeldet.
 *
 * Der Unterschied ist der ganze Punkt: ein neues `JWT_SECRET` meldet alle
 * Benutzer ab, ärgerlich für zehn Minuten. Ein neues `APP_SECRET` macht jede
 * gespeicherte Zugangsdatei dieser Instanz unlesbar — VLB, Shops, SMTP/IMAP —,
 * und zwar erst dann bemerkbar, wenn jemand die Funktion benutzt.
 *
 * Deshalb wird hier nicht nur gewarnt, sondern der Name abgefragt.
 */
const UNANTASTBAR: Record<string, string> = {
  APP_SECRET:
    'Verschlüsselt die gespeicherten Zugangsdaten (VLB, Shops, SMTP/IMAP). ' +
    'Ein neuer Wert macht sie unlesbar — sie müssten beim Anbieter rotiert und neu erfasst werden.',
  DB_PASSWORD:
    'Das laufende Postgres kennt nur das alte Passwort. Ein neuer Wert sperrt die Instanz aus ihren eigenen Daten aus.',
  OJFM_INTEGRATION_KMS_KEY:
    'Verschlüsselt die hinterlegten Zugangsdaten von Integrationen. Ein neuer Wert macht sie unlesbar.',
};

export default function InstanceDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  /**
   * Der offene Reiter steht in der Adresse, nicht nur im Zustand.
   *
   * Damit laesst sich eine Ansicht weitergeben — „schau dir die Zahlen an"
   * ist sonst ein Link auf die Uebersicht plus die Bitte, den richtigen
   * Reiter zu suchen. Ein unbekannter Wert faellt still auf die Uebersicht
   * zurueck, statt einen leeren Inhalt zu zeigen.
   *
   * `replace`, damit der Zurueck-Knopf nicht durch zehn Reiter laeuft,
   * bevor er die Seite verlaesst.
   */
  const [suchParams, setSuchParams] = useSearchParams();
  const reiterAusUrl = suchParams.get('reiter');
  const activeTab: Tab = ALLE_REITER.includes(reiterAusUrl as Tab)
    ? (reiterAusUrl as Tab)
    : 'overview';

  const setActiveTab = (wert: Tab) => {
    const neu = new URLSearchParams(suchParams);
    if (wert === 'overview') neu.delete('reiter');
    else neu.set('reiter', wert);
    setSuchParams(neu, { replace: true });
  };
  const [logJobId, setLogJobId] = useState<string | null>(null);
  const [secretKey, setSecretKey] = useState('');
  const [secretValue, setSecretValue] = useState('');
  const [secretError, setSecretError] = useState('');

  // ── Ein Geheimnis im Klartext ─────────────────────────────────────────────
  //
  // Der Weg dahin gab es immer: wer SSH auf den Wirt hat, liest die .env der
  // Instanz. Nur war er unkontrolliert — keine Berechtigung ausser dem Zugang,
  // keine Begruendung, keine Spur. Hier kostet er eine Begruendung und steht
  // danach im Protokoll.
  //
  // Der Wert bleibt bewusst im lokalen Zustand und geht nicht in den
  // Query-Cache: Wer ihn zweimal braucht, fragt zweimal — und das steht dann
  // auch zweimal im Protokoll.
  const [verrateSchluessel, setVerrateSchluessel] = useState<string | null>(null);
  const [verrateGrund, setVerrateGrund] = useState('');
  const [verrateWert, setVerrateWert] = useState<string | null>(null);
  const [verrateFehler, setVerrateFehler] = useState('');
  const [verrateLaeuft, setVerrateLaeuft] = useState(false);
  const [wertKopiert, setWertKopiert] = useState(false);

  const { data: instance, isLoading } = useQuery({
    queryKey: ['instances', id],
    queryFn: () => instances.get(id!),
    enabled: !!id,
  });

  const { data: jobs, isLoading: loadingJobs } = useQuery({
    queryKey: ['instances', id, 'jobs'],
    queryFn: () => deployments.list({ pageSize: 50 }),
    enabled: !!id && activeTab === 'jobs',
  });

  const { data: secretKeys, isLoading: loadingSecrets } = useQuery({
    queryKey: ['instances', id, 'secrets'],
    queryFn: () => instances.listSecretKeys(id!),
    enabled: !!id && activeTab === 'secrets',
  });

  const { data: logs } = useQuery({
    queryKey: ['instances', id, 'logs', logJobId],
    queryFn: () => instances.getLogs(id!),
    enabled: !!logJobId,
  });

  /**
   * Der Auftrag, der gerade verfolgt wird.
   *
   * Start, Stop, Restart und Deploy stellen alle nur einen Auftrag in die
   * Warteschlange -- der Aufruf ist nach Millisekunden zurueck, die Arbeit
   * dauert bis zu einer Minute. Ohne diesen Zustand sah man dazwischen nichts
   * und klickte im Zweifel noch einmal.
   */
  const [verfolgterJob, setVerfolgterJob] = useState<string | null>(null);
  const [entfernenOffen, setEntfernenOffen] = useState(false);
  const [umzugOffen, setUmzugOffen] = useState(false);
  const { user } = useAuth();
  const darfSql = user?.role === 'ADMIN' || user?.role === 'OPERATIONS';
  // Enger als `darfSql`, mit Absicht: die Namen der Geheimnisse sehen ADMIN und
  // OPERATIONS, die Werte nur ADMIN. Ein Geheimnis, das gelesen wurde, laesst
  // sich nicht zurueckholen.
  const darfGeheimnisseSehen = user?.role === 'ADMIN';

  function verrateSchliessen() {
    setVerrateSchluessel(null);
    setVerrateGrund('');
    setVerrateWert(null);
    setVerrateFehler('');
    setWertKopiert(false);
  }

  async function verrateAnfordern() {
    if (!verrateSchluessel || !id) return;
    setVerrateLaeuft(true);
    setVerrateFehler('');
    try {
      const r = await instances.revealSecret(id, verrateSchluessel, verrateGrund.trim());
      setVerrateWert(r.value);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string; error?: string } }; message?: string };
      setVerrateFehler(
        err.response?.data?.message || err.response?.data?.error || err.message || 'Fehlgeschlagen.',
      );
    } finally {
      setVerrateLaeuft(false);
    }
  }
  const [aktionsFehler, setAktionsFehler] = useState('');
  // null heisst „nicht angefasst" — dann zeigt das Feld den gespeicherten Wert.
  // Mit einem useEffect-Abgleich waere der Tippstand bei jedem Neuladen der
  // Abfrage ueberschrieben worden, mitten im Eintippen.
  const [uploadMbEntwurf, setUploadMbEntwurf] = useState<string | null>(null);
  const [uploadTimeoutEntwurf, setUploadTimeoutEntwurf] = useState<string | null>(null);

  /** Gleiches Verhalten fuer alle vier: Auftrag merken, Fehler anzeigen. */
  function jobMutation(fn: () => Promise<AuftragAngelegt>, fehlertext: string) {
    return {
      mutationFn: fn,
      onSuccess: (antwort: AuftragAngelegt) => {
        setAktionsFehler('');
        // `deploymentJobId`, nicht `id` -- so heisst das Feld in der Antwort.
        setVerfolgterJob(antwort.deploymentJobId);
        qc.invalidateQueries({ queryKey: ['instances', id] });
        qc.invalidateQueries({ queryKey: ['instances', id, 'jobs'] });
      },
      onError: (err: unknown) => {
        setVerfolgterJob(null);
        setAktionsFehler(
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
            fehlertext,
        );
      },
    };
  }

  const startMutation = useMutation(jobMutation(() => instances.start(id!), 'Starten fehlgeschlagen.'));
  const stopMutation = useMutation(jobMutation(() => instances.stop(id!), 'Stoppen fehlgeschlagen.'));
  const restartMutation = useMutation(
    jobMutation(() => instances.restart(id!), 'Neustarten fehlgeschlagen.'),
  );
  const deployMutation = useMutation(
    jobMutation(() => instances.deploy(id!), 'Ausrollen fehlgeschlagen.'),
  );

  /**
   * Der Blick vor dem Ausrollen.
   *
   * Der Knopf fragt erst nach: Steht eine neuere Vorlage bereit, fehlt ein
   * Bildtag, liegt ein Tag nicht in der Registry? Ist nichts davon der Fall,
   * rollt er sofort aus wie zuvor — ein Zwischenschritt, der auch im Normalfall
   * erscheint, wird nach der dritten Wiederholung weggeklickt und dann auch das
   * eine Mal, an dem er etwas zu sagen hatte.
   *
   * Scheitert die Nachfrage selbst, wird trotzdem ausgerollt. Sie ist eine
   * Zusatzauskunft; sie darf das Ausrollen nicht verhindern, nur weil die
   * Registry gerade nicht mag.
   */
  const [befundOffen, setBefundOffen] = useState<AusrollBefund | null>(null);

  const befundMutation = useMutation({
    mutationFn: () => instances.ausrollBefund(id!),
    onSuccess: (b) => {
      if (b.sauber) deployMutation.mutate();
      else setBefundOffen(b);
    },
    onError: () => deployMutation.mutate(),
  });

  /**
   * Produktion oder Abnahme setzen.
   *
   * Aenderbar und nicht nur beim Anlegen: bei allem, was vor diesem Feld
   * entstanden ist, steht nichts drin, und geraten wird hier bewusst nicht.
   * Ohne diesen Weg waere eine falsche oder fehlende Angabe nur ueber die
   * Datenbank zu korrigieren.
   */
  const umgebungMutation = useMutation({
    mutationFn: (wert: InstanceEnvironment) =>
      instances.update(id!, { environment: wert } as never),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['instances', id] });
      qc.invalidateQueries({ queryKey: ['instances'] });
    },
  });

  /**
   * Die Farbe der Instanz.
   *
   * Kein Formular, kein Speichern-Knopf: ein Klick setzt sie. Sie trägt keine
   * Logik, also wäre ein Bestätigungsschritt hier nur im Weg — und wer sich
   * verklickt, klickt einmal weiter.
   */
  const farbeMutation = useMutation({
    mutationFn: (wert: string | null) => instances.update(id!, { farbe: wert } as never),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['instances', id] });
      qc.invalidateQueries({ queryKey: ['instances'] });
    },
  });

  /**
   * Die Upload-Grenze dieser Instanz.
   *
   * Sie landet in `currentConfig` und von dort über `{{UPLOAD_MAX_MB}}` in die
   * `.env` des Stacks — die compose.yaml baut daraus den Proxy-Rahmen
   * (`npm.proxy.0.advanced.config`). Leer heißt: es gilt der Standard aus der
   * Vorlage, 2100 MB.
   *
   * Warum überhaupt hier und nicht im Proxy-Panel: `npm-docker-sync` legt den
   * Proxy-Host bei jedem Containerstart aus den Labels neu an. Was dort von
   * Hand steht, ist beim nächsten Ausrollen weg.
   */
  const uploadMutation = useMutation({
    mutationFn: (werte: Record<string, string>) =>
      instances.update(id!, {
        currentConfig: {
          ...((instance?.currentConfig ?? {}) as Record<string, unknown>),
          ...werte,
        },
      } as never),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['instances', id] });
      qc.invalidateQueries({ queryKey: ['instances'] });
    },
  });

  /**
   * Nachsehen, was auf dem Wirt wirklich läuft.
   *
   * Der Befund wird angezeigt und nicht bloss verschluckt: ein Knopf, der den
   * Zustand stillschweigend ändert, ist nicht nachvollziehbar — und einer, der
   * nichts ändert, ohne zu sagen warum, wird für kaputt gehalten.
   */
  const [befund, setBefund] = useState<ZustandsBefund | null>(null);
  const zustandMutation = useMutation({
    mutationFn: () => instances.zustandPruefen(id!),
    onSuccess: (b) => {
      setBefund(b);
      qc.invalidateQueries({ queryKey: ['instances', id] });
      qc.invalidateQueries({ queryKey: ['instances'] });
    },
    onError: () => setBefund(null),
  });

  /**
   * Zustand setzen.
   *
   * Nur die Werte aus `SETZBARE_ZUSTAENDE` -- die uebrigen gehoeren den
   * Hintergrundauftraegen. Ist der aktuelle Zustand einer davon, wird er
   * angezeigt und nicht zur Auswahl gestellt: mitten in einem laufenden Deploy
   * den Status umzustellen waere kein Bedienfehler, sondern ein Datenschaden.
   */
  /**
   * Vorlagenversion.
   *
   * Bisher nur beim Anlegen waehlbar -- eine bestehende Instanz blieb auf der
   * Version, mit der sie angelegt wurde, und jede spaetere Vorlage erreichte
   * sie nie. In der Produktion zeigte darum keine einzige Instanz auf die
   * aktuelle Fassung.
   *
   * Wirkt erst beim naechsten Ausrollen. Das ist Absicht: eine neue Vorlage
   * kann neue Pflichtwerte mitbringen, und die legt der Deploy an -- nicht
   * dieser Klick.
   */
  // Die Vorlagen der Application (Stufe 2) — bei einer Buendel-Instanz
  // haette das Produkt keine eindeutigen.
  const vorlagen = useQuery({
    queryKey: instance?.applicationId ? ['applications', instance.applicationId, 'templates'] : ['products', instance?.productId, 'templates'],
    queryFn: () => (instance!.applicationId ? applications.getTemplates(instance!.applicationId) : products.getTemplates(instance!.productId)),
    enabled: Boolean(instance?.productId),
  }).data;

  /**
   * Die Bildtags, nur fuer die Marke im Kopf.
   *
   * Gleicher Abfrageschluessel wie im BildtagPanel — react-query holt sie
   * dadurch einmal, nicht zweimal.
   */
  const bilder = useQuery({
    queryKey: ['instance-images', id],
    queryFn: () => instances.images(id!),
    enabled: Boolean(id),
  }).data;

  const vorlagenMutation = useMutation({
    mutationFn: (templateId: string) =>
      instances.update(id!, { currentTemplateId: templateId } as never),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['instances', id] });
      // Die Bildtags haengen an der Vorlage: eine andere Version kann andere
      // Images fuehren.
      qc.invalidateQueries({ queryKey: ['instance-images', id] });
    },
  });

  /**
   * Wer den A-Record pflegt.
   *
   * Wirkt beim nächsten Ausrollen: der PC setzt den Eintrag dann selbst, oder
   * der Beiwagen wird über `COMPOSE_PROFILES` zugeschaltet. Ein Wechsel
   * unterwegs ändert nichts an einem laufenden Container.
   */
  const dnsMutation = useMutation({
    mutationFn: (wert: string) => instances.update(id!, { dnsPflege: wert } as never),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['instances', id] }),
  });

  const statusMutation = useMutation({
    mutationFn: (wert: SetzbarerZustand) =>
      instances.update(id!, { status: wert } as never),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['instances', id] });
      qc.invalidateQueries({ queryKey: ['instances'] });
    },
  });

  const setSecretMutation = useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) =>
      instances.setSecret(id!, key, value),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['instances', id, 'secrets'] });
      setSecretKey('');
      setSecretValue('');
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Failed to set secret.';
      setSecretError(msg);
    },
  });

  const deleteSecretMutation = useMutation({
    mutationFn: (key: string) => instances.deleteSecret(id!, key),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['instances', id, 'secrets'] }),
  });

  if (isLoading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-8 bg-slate-200 rounded w-64" />
        <div className="h-4 bg-slate-100 rounded w-96" />
      </div>
    );
  }

  if (!instance) {
    return <div className="text-slate-500">{t('instances.notFound')}</div>;
  }

  const isSelfHosted = instance.hostingMode === 'SELF_HOSTED';
  /**
   * Beobachtet: laeuft ueber Portainer, der PC schaut nur zu.
   *
   * Die Knoepfe bleiben sichtbar und werden deaktiviert, statt zu
   * verschwinden. Der Unterschied ist hier wesentlich: Am 07.10.2026 hat ein
   * Kollege eine beobachtete Instanz wiederholt neu auszurollen versucht, weil
   * er nicht erkannte, dass sie ueber Portainer laeuft. Waeren die Knoepfe
   * einfach weg gewesen, haette er genauso lange gesucht -- nur ohne jeden
   * Hinweis, wonach.
   *
   * Ein ausgegrauter Knopf mit Begruendung im Titel beantwortet die Frage, die
   * tatsaechlich gestellt wird: nicht "wo ist der Knopf", sondern "warum
   * passiert nichts".
   */
  const istBeobachtet = instance.hostingMode === 'OBSERVED';
  const beobachtetGrund = istBeobachtet
    ? `Diese Instanz wird nur beobachtet — sie läuft über Portainer${
        instance.stackName ? ` als Stack „${instance.stackName}"` : ''
      }. Der PC rollt hier nichts aus, startet und stoppt nichts.`
    : undefined;
  // Die erste Stelle, an der die Art auf der Detailseite etwas bewirkt: bei
  // Produktinstanzen und Apps sind die Besucher angemeldet — das waere
  // Produktnutzung und keine Reichweite, und rechtlich eine andere Lage.
  const istWebseite = instance.art === 'WEBSEITE';
  const istBryve = instance.product?.name === 'Bryve';

  const tabs: { key: Tab; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    // Only meaningful when the server is not ours: for MANAGED instances we can
    // simply stop the container, so there is nothing to activate or revoke.
    ...(isSelfHosted ? [{ key: 'activation' as Tab, label: 'Aktivierung' }] : []),
    { key: 'metrics', label: 'Verläufe' },
    ...(istWebseite ? [{ key: 'statistik' as Tab, label: t('webstat.tab') }] : []),
    // ══ WAS EINE WEBSEITE NICHT HAT ════════════════════════════════════════
    //
    // Mandanten, Admin-Zugänge, Benutzerzugänge und Verknüpfungen setzen eine Anwendung
    // mit Benutzerverwaltung voraus. Eine Webseite hat keine — dort führen
    // diese Reiter in Ansichten, die nur erklären können, dass sie nichts
    // anzuzeigen haben.
    ...(istWebseite ? [] : [{ key: 'tenants' as Tab, label: 'Mandanten' }]),
    // Neben die Mandanten, nicht zu den Secrets: hier geht es um Menschen, die
    // sich anmelden, nicht um Werte in einer .env. Wer nur zusehen darf, hat
    // hier nichts zu tun — die Route verlangt ohnehin ADMIN/OPERATIONS/SUPPORT.
    // Neben „Admin-Zugänge", aus demselben Grund: beides setzt eine Datenbank voraus.
    // Bei einer Webseite gibt es nichts zu sichern, was hier hingehörte.
    ...(!istWebseite ? [{ key: 'sicherungen' as Tab, label: 'Sicherungen' }] : []),
    // Neben den Sicherungen, weil beides dieselbe Frage beantwortet: was,
    // wenn etwas weg ist. Auch fuer Webseiten — gerade fuer die: ihre Daten
    // sind Dateien, und die Sicherung nebenan kann nur Datenbanken.
    ...(darfSql ? [{ key: 'datenstaende' as Tab, label: 'Datenstände' }] : []),
    { key: 'mailgestaltung' as Tab, label: 'Mail-Gestaltung' },
    ...(darfSql && !istWebseite ? [{ key: 'zugaenge' as Tab, label: 'Admin-Zugänge' }] : []),
    // „Benutzerzugänge" und nicht „Nutzer": am Kunden heisst der Reiter „Mitarbeiter",
    // und hier stehen auch Konten, die keine Mitarbeiter des Kunden sind — allen
    // voran unser eigenes Betreiberkonto.
    //
    // Nur zeigen, wenn die Vorlage der Instanz es kann: eine aeltere Instanz hat
    // die noetigen Endpunkte nicht, und ein Reiter, der ins Leere greift, ist
    // schlimmer als keiner.
    ...(darfSql && !istWebseite && instance.template?.usersSupported
      ? [{ key: 'appnutzer' as Tab, label: 'Benutzerzugänge' }] : []),
    ...(istWebseite ? [] : [{ key: 'links' as Tab, label: 'Verknüpfungen' }]),
    // Die compose.yaml einer Webseite gehört ihr selbst — also gehört sie auch
    // in ihre Ansicht. Bei den übrigen Arten kommt sie aus einer Vorlage und
    // wird dort gepflegt, nicht je Instanz.
    ...(istWebseite ? [{ key: 'compose' as Tab, label: 'compose.yaml' }] : []),
    { key: 'jobs', label: 'Deployment Jobs' },
    // Neben den Jobs, weil man genau von dort kommt: der Deploy lief durch, die
    // Seite antwortet trotzdem nicht. Nur fuer die, die auch auf den Wirt
    // duerfen — Protokolle sind Betriebsdaten, keine Uebersicht.
    ...(darfSql ? [{ key: 'diagnose' as Tab, label: 'Diagnose' }] : []),
    // Nur fuer die, die auch ausrollen duerfen — und nur, wenn es ueberhaupt
    // eine Datenbank gibt. `hatDatenbank` kommt aus dem Backend und wird dort
    // an derselben Stelle erkannt wie von der SQL-Konsole selbst.
    ...(darfSql && instance.hatDatenbank ? [{ key: 'sql' as Tab, label: t('sql.tab') }] : []),
    { key: 'secrets', label: 'Secrets' },
    { key: 'config', label: 'Config' },
  ];

  // Auch waehrend ein Auftrag laeuft gesperrt, nicht nur waehrend des
  // Absendens. Zwei gleichzeitige Deploys auf derselben Instanz sind der Fall,
  // den man hinterher muehsam auseinandersortiert.
  const isBusy =
    startMutation.isPending ||
    stopMutation.isPending ||
    restartMutation.isPending ||
    deployMutation.isPending ||
    Boolean(verfolgterJob);

  // Ableitungen fuer den Kopf. Keine Hooks, darum hier unten unbedenklich.
  const umgebungDef = umgebung(instance.environment);
  // Das Backend-Bild steht fuer „was laeuft hier" — bei ojfm wie bei bryve ist
  // es das, was die Fassung bestimmt.
  const hauptBild =
    bilder?.find((b) => b.schluessel === 'BACKEND_IMAGE_TAG') ?? bilder?.[0] ?? null;

  // Die oeffentliche Adresse. Kein Hook, darum hier unten unbedenklich.
  const adresse = instanzAdresse(instance);

  return (
    <div className="space-y-6">
      {/* Back */}
      <button
        onClick={() => navigate('/instances')}
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft size={14} /> Back to Instances
      </button>

      {/* Header */}
      <div className="bg-surface border border-slate-200 rounded-xl p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              {/* Die Farbe sitzt vor dem Namen, weil sie in der Liste genau
                  dort steht: wer aus der Tabelle hierher kommt, findet dieselbe
                  Kennung an derselben Stelle wieder. Der Punkt ist zugleich der
                  Weg, sie zu ändern -- der Wähler klappt darunter auf, statt
                  dauerhaft die Kopfzeile zu belegen. */}
              <span className="inline-flex items-center gap-2">
                <FarbPunktSchalter
                  wert={instance.farbe}
                  onWahl={(farbe) => farbeMutation.mutate(farbe)}
                  titel={instance.stackName ?? instance.domain ?? instance.subdomain ?? undefined}
                />
                <h1 className="text-xl font-bold text-slate-900 font-mono">
                  {instance.domain ?? instance.subdomain ?? instance.id.slice(0, 12)}
                </h1>
              </span>
              <StatusBadge status={instance.status} type="instance" />

              {/* ══ NOCHMAL NACHSEHEN ═══════════════════════════════════════
                  Der Zustand entsteht sonst nur als Nebenwirkung des
                  Ausrollens. Wer nach einem gescheiterten Auftrag von Hand
                  nachbessert, lässt ein FAILED stehen, das nicht mehr stimmt —
                  und niemand sieht je wieder nach. */}
              <button
                type="button"
                onClick={() => zustandMutation.mutate()}
                disabled={zustandMutation.isPending}
                title="Auf dem Wirt nachsehen, was wirklich läuft, und den Zustand nachziehen"
                className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2 py-0.5 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              >
                <RotateCcw size={11} className={zustandMutation.isPending ? 'animate-spin' : ''} />
                {zustandMutation.isPending ? 'sehe nach…' : 'neu prüfen'}
              </button>
              {/* Umgebung — dieselbe Farbgebung wie in der Tabelle, damit man
                  beim Wechsel zwischen Liste und Seite nicht umdenkt. */}
              {umgebungDef && (
                <span
                  title={t(`environment.${umgebungDef.schluessel}Hint`)}
                  className={`rounded-lg px-2 py-0.5 text-xs font-medium ring-1 ${umgebungDef.farbe}`}
                >
                  {t(`environment.${umgebungDef.schluessel}`)}
                </span>
              )}
              {/* Was hier läuft.
                  Ist ein Release gesetzt, ist das die Antwort. Ist keines
                  gesetzt, ist die Antwort trotzdem nicht „nichts" — dann
                  bestimmt der wirksame Bildtag, was gezogen wird, und den zu
                  zeigen ist ehrlicher als eine leere Stelle. */}
              {instance.currentRelease ? (
                <span
                  title={t('headline.releaseHint', { channel: instance.currentRelease.releaseChannel })}
                  className="rounded-lg bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700 ring-1 ring-sky-200"
                >
                  {instance.currentRelease.version}
                  {instance.currentRelease.releaseName ? ` · ${instance.currentRelease.releaseName}` : ''}
                </span>
              ) : (
                hauptBild && (
                  <span
                    title={t('headline.noReleaseHint', {
                      dienst: hauptBild.dienst,
                      herkunft: t(`images.from${
                        hauptBild.herkunft === 'instanz'
                          ? 'Instance'
                          : hauptBild.herkunft === 'standard'
                            ? 'Default'
                            : 'Open'
                      }`),
                    })}
                    className="rounded-lg bg-slate-100 px-2 py-0.5 font-mono text-xs font-medium text-slate-600 ring-1 ring-slate-200"
                  >
                    {hauptBild.wirksam ?? t('images.fromOpen')}
                  </span>
                )
              )}
              {/* Der Weg hin. Steht im Kopf, weil man ihn dort sucht — und
                  nicht erst nach dem Öffnen eines Reiters. */}
              {adresse && (
                <a
                  href={adresse}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={adresse}
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2 py-0.5 text-xs text-brand-700 hover:bg-slate-50"
                >
                  {t('instances.open')} <ExternalLink size={11} />
                </a>
              )}
              {/* ══ WEITERE ADRESSEN ═══════════════════════════════════════
                  Eine Instanz hat oft mehr als einen Namen: die Kundenadresse
                  unter juni.cloud, pgAdmin, was sonst noch hinterlegt ist. Sie
                  standen bisher nirgends -- man musste die Compose-Datei lesen,
                  um zu wissen, wie der Kunde die Instanz aufruft.

                  Der Punkt davor traegt die Farbe der Instanz, damit die Karte
                  dieselbe Kennung zeigt wie die Zeile in der Liste. */}
              {instance.weitereAdressen?.map((a) => (
                <a
                  key={a.id ?? a.hostname}
                  href={`https://${a.hostname}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={a.notiz ? `${a.hostname} — ${a.notiz}` : a.hostname}
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2 py-0.5 text-xs text-slate-600 hover:bg-slate-50"
                >
                  {a.notiz ?? a.hostname} <ExternalLink size={11} />
                </a>
              ))}

              {isSelfHosted && (
                <span
                  className="rounded-lg border border-violet-200 bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-700"
                  title="Läuft auf Hardware des Kunden. Kein SSH-Zugriff garantiert — Verwaltung über Aktivierung und Agent."
                >
                  Fremd-Hosting
                </span>
              )}
            </div>
            <div className="flex gap-3 mt-1.5 text-sm text-slate-500 flex-wrap">
              <span>{instance.customer?.companyName ?? instance.customerId.slice(0, 8)}</span>
              <span>·</span>
              <span>{instance.product?.name ?? instance.productId.slice(0, 8)}</span>
              {instance.plan && <><span>·</span><span>{instance.plan.name}</span></>}
              {instance.server && <><span>·</span><span className="font-mono text-xs">{instance.server.hostname}</span></>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/*
              Steht bei den Aktionen und nicht in einem Reiter weiter unten:
              „wer war wann an dieser Instanz" ist eine Frage, die unter Druck
              gestellt wird — von einem Kunden, der etwas wissen will. Dann
              sucht niemand.
            */}
            <Link
              to={`/audit?entityType=Instance&entityId=${instance.id}`}
              title="Alle protokollierten Zugriffe auf diese Instanz — mit CSV-Export"
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-300 text-slate-700 text-sm rounded-lg hover:bg-slate-50"
            >
              <ScrollText size={13} /> Protokoll
            </Link>
            <button
              onClick={() => startMutation.mutate()}
              disabled={isBusy || istBeobachtet}
              title={beobachtetGrund}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded-lg disabled:opacity-50"
            >
              <Play size={13} /> Start
            </button>
            <button
              onClick={() => { if (confirm('Stop this instance?')) stopMutation.mutate(); }}
              disabled={isBusy || istBeobachtet}
              title={beobachtetGrund}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm rounded-lg disabled:opacity-50"
            >
              <Square size={13} /> Stop
            </button>
            <button
              onClick={() => restartMutation.mutate()}
              disabled={isBusy || istBeobachtet}
              title={beobachtetGrund}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-slate-600 hover:bg-slate-700 text-white text-sm rounded-lg disabled:opacity-50"
            >
              <RotateCcw size={13} /> Restart
            </button>
            <button
              onClick={() => befundMutation.mutate()}
              disabled={isBusy || befundMutation.isPending || istBeobachtet}
              title={beobachtetGrund}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm rounded-lg disabled:opacity-50"
            >
              <Rocket size={13} /> Deploy
            </button>
            <button
              onClick={() => setUmzugOffen(true)}
              disabled={isBusy || istBeobachtet}
              title={beobachtetGrund ?? t('migrate.title')}
              className="ml-2 inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <Truck size={13} /> {t('migrate.action')}
            </button>
            {/* Abgesetzt und rot umrandet statt gefuellt: es soll auffindbar
                sein, aber nicht neben „Deploy" wie eine gleichwertige Aktion
                aussehen. */}
            <button
              onClick={() => setEntfernenOffen(true)}
              disabled={isBusy}
              title={t('remove.title')}
              className="ml-2 inline-flex items-center gap-1.5 rounded-lg border border-red-300 px-3 py-2 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 size={13} /> {t('remove.action')}
            </button>
          </div>
        </div>
      </div>

      {/* Steht ueber dem Fehlerbereich und nicht darunter: Wer hier landet,
          soll die Erklaerung lesen, bevor er klickt -- nicht danach. */}
      {istBeobachtet && (
        <div className="flex items-start gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900">
          <Eye size={15} className="mt-0.5 shrink-0" />
          <div>
            <span className="font-medium">Diese Instanz wird nur beobachtet.</span>{' '}
            Sie läuft über <strong>Portainer</strong>
            {instance.stackName && (
              <> als Stack <code className="rounded bg-sky-100 px-1 text-xs">{instance.stackName}</code></>
            )}
            {instance.server && <> auf {instance.server.hostname}</>}. Der PC führt sie in
            der Liste, damit sichtbar ist, was wo läuft — er rollt hier aber nichts aus,
            startet und stoppt nichts. <strong>Änderungen gehören nach Portainer.</strong>
          </div>
        </div>
      )}

      {/* Direkt unter den Knoepfen, nicht am Seitenende: der Blick ist nach dem
          Klick dort, und eine Rueckmeldung, die man suchen muss, ist keine. */}
      {aktionsFehler && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {aktionsFehler}
        </div>
      )}

      {/* Der Befund der Zustandsprüfung. Grün nur, wenn wirklich alles läuft —
          nicht schon dann, wenn die Prüfung durchlief. */}
      {befund && (
        <div
          className={`rounded-lg border px-3 py-2 text-sm ${
            befund.geaendert
              ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
              : befund.ok
                ? 'border-slate-200 bg-slate-50 text-slate-700'
                : 'border-amber-200 bg-amber-50 text-amber-900'
          }`}
        >
          <div className="flex items-start justify-between gap-3">
            <span>{befund.meldung}</span>
            <button
              type="button"
              onClick={() => setBefund(null)}
              className="shrink-0 text-xs opacity-60 hover:opacity-100"
            >
              ✕
            </button>
          </div>

          {/* Die Rohdaten darunter: wer den Zustand anzweifelt, will sehen,
              woraus er abgeleitet wurde — nicht nur das Urteil. */}
          {befund.container && befund.container.zeilen.length > 0 && (
            <ul className="mt-2 space-y-0.5 font-mono text-xs opacity-80">
              {befund.container.zeilen.map((z) => (
                <li key={z.name}>{z.name} — {z.status}</li>
              ))}
            </ul>
          )}
          {befund.adressen.length > 0 && (
            <ul className="mt-2 space-y-0.5 font-mono text-xs opacity-80">
              {befund.adressen.map((a) => (
                <li key={a.adresse}>{a.code ?? 'keine Antwort'} — {a.adresse}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {verfolgterJob && (
        <JobVerlauf
          jobId={verfolgterJob}
          onSchliessen={() => setVerfolgterJob(null)}
          onFertig={() => {
            qc.invalidateQueries({ queryKey: ['instances', id] });
            qc.invalidateQueries({ queryKey: ['instances', id, 'jobs'] });
          }}
        />
      )}

      {befundOffen && (
        <AusrollPanel
          befund={befundOffen}
          produktId={instance.productId}
          umstellenLaeuft={vorlagenMutation.isPending}
          onVorlageWaehlen={(templateId) =>
            // Nach dem Umstellen neu nachfragen statt das Panel zu schliessen:
            // die neue Vorlage kann eigene Befunde mitbringen, und wer gerade
            // umgestellt hat, will genau die sehen.
            vorlagenMutation.mutate(templateId, { onSuccess: () => befundMutation.mutate() })
          }
          onAusrollen={() => {
            setBefundOffen(null);
            deployMutation.mutate();
          }}
          onSchliessen={() => setBefundOffen(null)}
        />
      )}

      {umzugOffen && (
        <UmzugModal
          instanz={instance}
          onSchliessen={() => setUmzugOffen(false)}
          onEingereiht={(auftrag) => {
            setUmzugOffen(false);
            setVerfolgterJob(auftrag.deploymentJobId);
            qc.invalidateQueries({ queryKey: ['instances', id] });
          }}
        />
      )}

      {entfernenOffen && (
        <InstanzEntfernenModal
          instanz={instance}
          onSchliessen={() => setEntfernenOffen(false)}
          onEingereiht={(auftrag) => {
            setEntfernenOffen(false);
            setVerfolgterJob(auftrag.deploymentJobId);
            qc.invalidateQueries({ queryKey: ['instances', id] });
            qc.invalidateQueries({ queryKey: ['instances'] });
          }}
        />
      )}

      {!istWebseite && (
        <VerknuepfungsLeiste instanceId={instance.id} onOeffnen={() => setActiveTab('links')} />
      )}

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <div className="flex gap-1">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === t.key
                  ? 'border-brand-600 text-brand-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Overview tab */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-surface border border-slate-200 rounded-xl p-5 space-y-3">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('instances.info')}</h3>
            {[
              { label: 'ID', value: instance.id },
              { label: 'Stack Name', value: instance.stackName ?? '—' },
              { label: t('instances.address'), value: '__ADRESSE__' },
              { label: 'Subdomain', value: instance.subdomain ?? '—' },
              { label: 'Domain', value: instance.domain ?? '—' },
              { label: 'Release Channel', value: instance.releaseChannel },
              { label: t('instanceStatus.label'), value: '__STATUS__' },
              { label: t('environment.label'), value: '__UMGEBUNG__' },
              { label: t('dns.label'), value: '__DNS__' },
              { label: t('templateVersion.label'), value: '__VORLAGE__' },
            ].map(({ label, value }) => (
              <div key={label} className="flex items-center justify-between gap-2 text-sm">
                <span className="text-slate-500">{label}</span>
                {value === '__DNS__' ? (
                  <select
                    value={instance.dnsPflege}
                    disabled={dnsMutation.isPending}
                    onChange={(e) => dnsMutation.mutate(e.target.value)}
                    title={t(`dns.${instance.dnsPflege.toLowerCase()}Hint`)}
                    className="rounded border border-slate-300 px-1.5 py-0.5 text-xs disabled:opacity-50"
                  >
                    {(['PC', 'BEIWAGEN', 'MANUELL'] as const).map((w) => (
                      <option key={w} value={w}>
                        {t(`dns.${w.toLowerCase()}`)}
                      </option>
                    ))}
                  </select>
                ) : value === '__ADRESSE__' ? (
                  adresse ? (
                    <a
                      href={adresse}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex max-w-[15rem] items-center gap-1 truncate text-xs text-brand-600 hover:underline"
                      title={adresse}
                    >
                      <span className="truncate">{adresse.replace(/^https:\/\//, '')}</span>
                      <ExternalLink size={12} className="shrink-0" />
                    </a>
                  ) : (
                    // Keine Domain heisst kein Link. Ein grauer Strich sagt das
                    // deutlicher als ein Link, der ins Leere fuehrt.
                    <span className="text-xs text-slate-400">{t('instances.noAddress')}</span>
                  )
                ) : value === '__STATUS__' ? (
                  istSetzbar(instance.status) ? (
                    <select
                      value={instance.status}
                      disabled={statusMutation.isPending}
                      onChange={(e) => {
                        const wert = e.target.value as SetzbarerZustand;
                        // Nur beim vertraglichen Ende nachfragen. Bei den
                        // uebrigen waere eine Rueckfrage Zeremonie -- sie sind
                        // in einem Klick wieder umgestellt.
                        if (wert === 'TERMINATED' && !confirm(t('instanceStatus.confirmTerminated')))
                          return;
                        statusMutation.mutate(wert);
                      }}
                      className="rounded border border-slate-300 px-1.5 py-0.5 text-xs disabled:opacity-50"
                    >
                      {SETZBARE_ZUSTAENDE.map((z) => (
                        <option key={z.wert} value={z.wert}>
                          {t(`instanceStatus.${z.schluessel}`)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span
                      title={t('instanceStatus.machineState')}
                      className="text-right font-mono text-xs text-slate-500"
                    >
                      {instance.status}
                    </span>
                  )
                ) : value === '__VORLAGE__' ? (
                  <div className="flex items-center gap-1">
                    <select
                      value={instance.currentTemplateId ?? ''}
                      disabled={vorlagenMutation.isPending}
                      onChange={(e) => vorlagenMutation.mutate(e.target.value)}
                      className="rounded border border-slate-300 px-1.5 py-0.5 text-xs disabled:opacity-50"
                    >
                      {/* Nur solange keine gesetzt ist. Eine Instanz ohne
                          Vorlage rollt gegen einen Platzhalter aus -- das ist
                          kein Zustand, den man aktiv waehlen sollte. */}
                      {!instance.currentTemplateId && (
                        <option value="">{t('templateVersion.unset')}</option>
                      )}
                      {(vorlagen ?? []).map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.version}
                          {v.status === 'ACTIVE' ? ` · ${t('templateVersion.active')}` : ''}
                        </option>
                      ))}
                    </select>
                    {/* Der Hinweis steht nur, wenn er zutrifft. Eine Warnung,
                        die immer da ist, liest niemand mehr. */}
                    {vorlagen?.some((v) => v.status === 'ACTIVE' && v.id !== instance.currentTemplateId) && (
                      <span title={t('templateVersion.olderHint')} className="text-amber-600">
                        <AlertTriangle size={13} />
                      </span>
                    )}
                  </div>
                ) : value === '__UMGEBUNG__' ? (
                  <select
                    value={instance.environment ?? ''}
                    disabled={umgebungMutation.isPending}
                    onChange={(e) =>
                      umgebungMutation.mutate(e.target.value as InstanceEnvironment)
                    }
                    className="rounded border border-slate-300 px-1.5 py-0.5 text-xs disabled:opacity-50"
                  >
                    {/* Der leere Eintrag steht nur, solange nichts gesetzt ist:
                        „nicht festgelegt" ist ein Zustand, aber keine Wahl, die
                        man aktiv treffen sollte. */}
                    {!instance.environment && (
                      <option value="">{t('environment.unset')}</option>
                    )}
                    {UMGEBUNGEN.map((u) => (
                      <option key={u.wert} value={u.wert}>
                        {t(`environment.${u.schluessel}`)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="max-w-xs truncate text-right font-mono text-xs text-slate-800">
                    {value}
                  </span>
                )}
              </div>
            ))}
          </div>
          <div className="bg-surface border border-slate-200 rounded-xl p-5 space-y-3">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Timestamps</h3>
            {[
              { label: 'Created', value: format(new Date(instance.createdAt), 'PPp') },
              { label: 'Updated', value: format(new Date(instance.updatedAt), 'PPp') },
              { label: 'Deployed', value: instance.deployedAt ? format(new Date(instance.deployedAt), 'PPp') : '—' },
              { label: 'Last Changed', value: instance.lastChangedAt ? format(new Date(instance.lastChangedAt), 'PPp') : '—' },
              { label: 'Suspended', value: instance.suspendedAt ? format(new Date(instance.suspendedAt), 'PPp') : '—' },
              { label: 'Termination Date', value: instance.terminationDate ? format(new Date(instance.terminationDate), 'PPp') : '—' },
            ].map(({ label, value }) => (
              <div key={label} className="flex justify-between text-sm">
                <span className="text-slate-500">{label}</span>
                <span className="text-slate-800 text-xs">{value}</span>
              </div>
            ))}
          </div>

          {/* Bildtags — über beide Spalten. Sie gehören auf die Übersicht und
              nicht unter „Konfiguration": wer sie braucht, sucht sie unter
              Zeitdruck, weil gerade etwas kaputt ist. */}
          {/* Adressen samt pgAdmin-Link. Ueber beide Spalten, direkt vor den
              Bildtags: beides sucht man unter Zeitdruck. */}
          <div className="md:col-span-2">
            <AdressenKarte instanceId={instance.id} />
          </div>

          <div className="md:col-span-2">
            <BildtagPanel instanceId={instance.id} />
          </div>

          {(instance.application?.slug ?? instance.product?.slug) === 'medusa' && (
            <div className="md:col-span-2">
              <MedusaSchluesselKarte instanceId={instance.id} darf={darfSql} />
            </div>
          )}

          {/* Verlagsverbund: nur bei eLibraries und nur für Rollen, die den
              Verbund sehen — sonst rendert die Karte nichts. */}
          <div className="md:col-span-2 empty:hidden">
            <VerbundKnotenKarte instanceId={instance.id} />
          </div>

          {/* Demo-Inhalte — nur bei Demo- und Testinstanzen.
              Die Bedingung steht hier UND im Backend. Hier, damit auf einer
              Produktivinstanz nicht dauerhaft ein Kasten steht, der erklaert,
              warum er nichts anbietet; im Backend, weil eine Pruefung, die nur
              in der Oberflaeche stattfindet, keine Pruefung ist. */}
          {(instance.environment === 'DEMO' || instance.environment === 'TEST') && (
            <div className="md:col-span-2">
              <DemoInhalteKarte instanceId={instance.id} />
            </div>
          )}
        </div>
      )}

      {/* Activation tab — self-hosted only */}
      {activeTab === 'activation' && isSelfHosted && <ActivationPanel instance={instance} />}

      {/* Mandanten tab */}
      {/* Inline statt als Fenster: auf der Instanzseite ist der Verlauf ein
          Reiter neben den anderen, kein Nachschlagewerk daneben. */}
      {activeTab === 'metrics' && (
        <MetrikPanel
          instanceId={instance.id}
          titel={instance.stackName ?? instance.id}
        />
      )}

      {/* Reichweite der Webseite. Die Bedingung steht doppelt — wie bei der
          Aktivierung: der Reiter laesst sich auch ueber die URL waehlen, und
          dann waere die Art noch nicht geprueft. */}
      {activeTab === 'statistik' && istWebseite && (
        <StatistikPanel instanceId={instance.id} stackName={instance.stackName} />
      )}

      {activeTab === 'tenants' && <TenantsPanel instanceId={instance.id} />}

      {/* Betreiber- und Kundenkonto samt Zugangslink */}
      {activeTab === 'zugaenge' && (
        <div className="space-y-3">
          <ZugaengePanel instanceId={instance.id} />
          {/* Nur bei bryve: der Weg dahinter kennt genau dessen Schnittstelle
              und dessen Tabellen. Fuer ojfm steht das Zuruecksetzen des
              Betreiberkontos oben im ZugaengePanel. */}
          {istBryve && <BryveTfaPanel instanceId={instance.id} />}
          {istBryve && <BryveNotfallPanel instanceId={instance.id} />}
          {/* Erscheint nur, wenn Bitwarden eingerichtet ist. */}
          <BitwardenKnopf instanceId={instance.id} />
        </div>
      )}

      {activeTab === 'appnutzer' && <AppNutzerPanel instanceId={instance.id} />}

      {/* Verknüpfungen zu anderen Instanzen desselben Kunden */}
      {activeTab === 'links' && <InstanceLinksPanel instanceId={instance.id} />}

      {/* Nur bei Webseiten: bei den anderen Arten kommt die compose.yaml aus
          einer Vorlage und wird dort gepflegt, nicht je Instanz. */}
      {activeTab === 'compose' && istWebseite && <ComposePanel instance={instance} />}
      {activeTab === 'mailgestaltung' && (
        <MailGestaltungPanel ziel={{ art: 'instanz', id: instance.id }} />
      )}

      {activeTab === 'sicherungen' && (
        <SicherungenPanel instanceId={instance.id} stackName={instance.stackName ?? ''} />
      )}

      {activeTab === 'diagnose' && <DiagnosePanel instanceId={instance.id} />}

      {activeTab === 'datenstaende' && <DatenStaendePanel instanceId={instance.id} />}

      {/* Jobs tab */}
      {activeTab === 'sql' && <SqlKonsole instanz={instance} />}

      {activeTab === 'jobs' && (
        <div className="space-y-3">
          {loadingJobs ? (
            <div className="animate-pulse space-y-2">
              {[1, 2, 3].map((i) => <div key={i} className="h-12 bg-slate-100 rounded-lg" />)}
            </div>
          ) : !jobs || jobs.length === 0 ? (
            <p className="text-slate-400 italic text-sm">{t('instances.noJobs')}</p>
          ) : (
            jobs.slice(0, 20).map((job) => (
              <div key={job.id} className="bg-surface border border-slate-200 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <StatusBadge status={job.status} type="job" />
                    <span className="text-sm font-mono text-slate-700">{job.type.replace(/_/g, ' ')}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-400">
                    {job.createdAt && <span>{format(new Date(job.createdAt), 'PPp')}</span>}
                    <button
                      onClick={() => setLogJobId(logJobId === job.id ? null : job.id)}
                      className="text-brand-600 hover:text-brand-800"
                    >
                      {logJobId === job.id ? 'Hide logs' : 'View logs'}
                    </button>
                  </div>
                </div>
                {job.errorMessage && (
                  <p className="mt-2 text-xs text-red-600">{job.errorMessage}</p>
                )}
                {logJobId === job.id && (
                  <pre className="mt-3 text-xs bg-konsole text-konsole-gruen p-3 rounded overflow-x-auto max-h-64 font-mono">
                    {logs ?? 'No logs available.'}
                  </pre>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Secrets tab */}
      {activeTab === 'secrets' && (
        <div className="space-y-4">
          <div className="bg-surface border border-slate-200 rounded-xl">
            <div className="px-5 py-4 border-b border-slate-100">
              <h3 className="text-sm font-semibold text-slate-700">{t('instances.secretKeys')}</h3>
              <p className="text-xs text-slate-400 mt-0.5">{t('instances.secretsHint')}</p>
            </div>
            {loadingSecrets ? (
              <div className="p-5 animate-pulse space-y-2">
                {[1, 2, 3].map((i) => <div key={i} className="h-8 bg-slate-100 rounded" />)}
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {secretKeys?.length === 0 && (
                  <li className="px-5 py-4 text-sm text-slate-400 italic">{t('instances.noSecrets')}</li>
                )}
                {secretKeys?.map((s) => (
                  <li key={s.id} className="px-5 py-3 flex items-center justify-between">
                    <code className="text-sm text-slate-700 font-mono">{s.key}</code>
                    {UNANTASTBAR[s.key] && (
                      <span
                        title={UNANTASTBAR[s.key]}
                        className="ml-2 inline-flex items-center gap-1 rounded bg-red-50 px-1.5 py-0.5 text-[11px] font-medium text-red-700 ring-1 ring-red-200"
                      >
                        <Lock size={11} /> {t('secrets.doNotRotate')}
                      </span>
                    )}
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-400">
                        v{s.version}
                        {s.lastUsedAt && ` · used ${format(new Date(s.lastUsedAt), 'PP')}`}
                      </span>
                      {darfGeheimnisseSehen && (
                        <button
                          onClick={() => { verrateSchliessen(); setVerrateSchluessel(s.key); }}
                          title="Wert im Klartext anzeigen — mit Begründung, wird protokolliert"
                          className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                        >
                          <Eye size={12} /> Anzeigen
                        </button>
                      )}
                      <button
                        onClick={() => {
                          // Bei den unantastbaren Werten reicht ein Klick nicht.
                          // Ein geloeschtes APP_SECRET wird beim naechsten
                          // Ausrollen neu erzeugt -- und macht damit alles
                          // unlesbar, was mit dem alten verschluesselt wurde.
                          const grund = UNANTASTBAR[s.key];
                          if (grund) {
                            if (prompt(t('secrets.typeKeyToDelete', { key: s.key }) + '\n\n' + grund) !== s.key)
                              return;
                          } else if (!confirm(`Delete secret "${s.key}"?`)) return;
                          deleteSecretMutation.mutate(s.key);
                        }}
                        disabled={deleteSecretMutation.isPending}
                        className="text-slate-400 hover:text-red-600 disabled:opacity-40"
                        title={t('instances.deleteSecret')}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Add secret form */}
          <div className="bg-surface border border-slate-200 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">
              <Plus size={14} className="inline mr-1" />
              Add / Update Secret
            </h3>
            {secretError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm mb-3">{secretError}</div>
            )}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setSecretError('');
                setSecretMutation.mutate({ key: secretKey, value: secretValue });
              }}
              className="flex gap-2 flex-wrap"
            >
              <input
                required
                placeholder="KEY_NAME"
                value={secretKey}
                onChange={(e) => setSecretKey(e.target.value)}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500 flex-1 min-w-32"
              />
              <input
                required
                type="password"
                placeholder={t('instances.secretValue')}
                value={secretValue}
                onChange={(e) => setSecretValue(e.target.value)}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 flex-1 min-w-48"
              />
              <button
                type="submit"
                disabled={setSecretMutation.isPending}
                className="px-4 py-2 text-sm bg-brand-600 text-white rounded-lg disabled:opacity-60"
              >
                {setSecretMutation.isPending ? 'Saving…' : 'Save'}
              </button>
            </form>
          </div>
        </div>
      )}


      {/* ── Ein Geheimnis im Klartext ──────────────────────────────────────
          Zwei Schritte mit Absicht: erst die Begruendung, dann der Wert. Wer
          nur nachsehen wollte, ob es den Schluessel gibt, braucht den Wert
          nicht — und ein Klick allein soll ihn nicht auf den Bildschirm
          holen, wo jemand mitliest. */}
      {verrateSchluessel && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          onClick={verrateSchliessen}
        >
          <div
            className="w-full max-w-lg rounded-xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-slate-100 px-5 py-4">
              <h3 className="text-sm font-semibold text-slate-800">
                Geheimnis anzeigen: <code className="font-mono">{verrateSchluessel}</code>
              </h3>
              <p className="mt-1 text-xs text-slate-500">
                Der Zugriff wird mit Ihrem Namen, dem Zeitpunkt und der Begründung protokolliert.
                Der Wert selbst steht in keinem Protokoll.
              </p>
            </div>

            <div className="space-y-4 px-5 py-4">
              {verrateWert === null ? (
                <>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-600">
                      Wofür wird der Wert gebraucht?
                    </label>
                    <textarea
                      autoFocus
                      rows={3}
                      value={verrateGrund}
                      onChange={(e) => setVerrateGrund(e.target.value)}
                      placeholder="z. B. Wartung an der Datenbank von juni-prod-bryve, Ticket 1234"
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none"
                    />
                    <p className="mt-1 text-[11px] text-slate-400">
                      Mindestens 10 Zeichen. Der Satz steht später im Protokoll — er beantwortet
                      die Frage, die jemand stellt, wenn er den Eintrag findet.
                    </p>
                  </div>

                  {/* Ein Kundenpasswort ist kein Betriebsgeheimnis. */}
                  {(verrateSchluessel === 'ADMIN_PASSWORD'
                    || verrateSchluessel === 'CUSTOMER_ADMIN_PASSWORD') && (
                    <div className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
                      <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                      <span>
                        Das ist das Anmeldekennwort eines Menschen, nicht ein technischer
                        Schlüssel. Wer es liest, hat eine zweite Kopie eines fremden Zugangs.
                        Oft ist ein neues Kennwort der sauberere Weg.
                      </span>
                    </div>
                  )}

                  {verrateFehler && (
                    <div className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
                      {verrateFehler}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-600">Wert</label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 break-all rounded-lg bg-slate-50 px-3 py-2 font-mono text-sm text-slate-800">
                        {verrateWert}
                      </code>
                      <button
                        onClick={() => {
                          navigator.clipboard?.writeText(verrateWert).then(
                            () => { setWertKopiert(true); setTimeout(() => setWertKopiert(false), 2000); },
                            () => { /* ohne Zwischenablage bleibt der Wert lesbar — das genuegt */ },
                          );
                        }}
                        className="shrink-0 rounded-lg border border-slate-200 px-2 py-2 text-slate-500 hover:bg-slate-50"
                        title="In die Zwischenablage"
                      >
                        {wertKopiert ? <Check size={14} /> : <Copy size={14} />}
                      </button>
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-400">
                    Dieses Fenster hält den Wert nur, solange es offen ist. Beim Schließen ist er
                    weg — ein zweiter Blick kostet eine zweite Begründung.
                  </p>
                </>
              )}
            </div>

            <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-3">
              <button
                onClick={verrateSchliessen}
                className="rounded-lg px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100"
              >
                {verrateWert === null ? 'Abbrechen' : 'Schließen'}
              </button>
              {verrateWert === null && (
                <button
                  onClick={verrateAnfordern}
                  disabled={verrateGrund.trim().length < 10 || verrateLaeuft}
                  className="rounded-lg bg-slate-800 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-900 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {verrateLaeuft ? 'Einen Moment…' : 'Anzeigen und protokollieren'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Config tab */}
      {activeTab === 'config' && (
        <div className="space-y-4">
          <ProduktKonfigKarte
            instanceId={instance.id}
            productId={instance.productId}
            applicationId={instance.applicationId}
            config={(instance.currentConfig ?? {}) as Record<string, unknown>}
          />
          <UploadGrenzeKarte
            config={(instance.currentConfig ?? {}) as Record<string, unknown>}
            mbEntwurf={uploadMbEntwurf}
            timeoutEntwurf={uploadTimeoutEntwurf}
            setMbEntwurf={setUploadMbEntwurf}
            setTimeoutEntwurf={setUploadTimeoutEntwurf}
            speichern={(werte) =>
              uploadMutation.mutate(werte, {
                onSuccess: () => {
                  setUploadMbEntwurf(null);
                  setUploadTimeoutEntwurf(null);
                },
              })
            }
            laeuft={uploadMutation.isPending}
            fehler={uploadMutation.isError}
          />

          <div className="bg-surface border border-slate-200 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">{t('instances.currentConfig')}</h3>
            <pre className="text-xs bg-konsole text-konsole-gruen p-4 rounded overflow-x-auto font-mono">
              {JSON.stringify(instance.currentConfig, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
